import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useCasos, useCrearExperimento, usePromptsDelLaboratorio, useSeccionesRedactables } from "@/hooks/useLaboratorio";
import { getApiErrorMessage } from "@/lib/api";

/** Cuántos casos admite un experimento (el mismo tope del servidor). */
export const TOPE_DE_CASOS = 10;

/** El prompt genérico sirve para cualquier sección: hay que elegir con cuál probarlo. */
const PROMPT_GENERICO = "redactar-seccion";

/**
 * Lanza un experimento: el borrador de un prompt contra el publicado, sobre
 * casos congelados. Solo se ofrecen los prompts que tienen borrador.
 */
export function NuevoExperimentoDialog({
  abierto,
  funcionInicial,
  onCerrar,
  onLanzado,
}: {
  abierto: boolean;
  funcionInicial: number | null;
  onCerrar: () => void;
  onLanzado: (experimentoId: number) => void;
}) {
  if (!abierto) return null;

  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="sm:max-w-lg">
        <Formulario funcionInicial={funcionInicial} onCerrar={onCerrar} onLanzado={onLanzado} />
      </DialogContent>
    </Dialog>
  );
}

function Formulario({
  funcionInicial,
  onCerrar,
  onLanzado,
}: {
  funcionInicial: number | null;
  onCerrar: () => void;
  onLanzado: (experimentoId: number) => void;
}) {
  const prompts = usePromptsDelLaboratorio();
  const secciones = useSeccionesRedactables();
  const casos = useCasos();
  const crear = useCrearExperimento();

  const [funcionId, setFuncionId] = useState(funcionInicial === null ? "" : String(funcionInicial));
  const [seccion, setSeccion] = useState("");
  const [elegidos, setElegidos] = useState<number[]>([]);

  const probables = (prompts.data?.data ?? []).filter((p) => p.admite_experimentos && p.tiene_borrador);
  const funcion = probables.find((p) => String(p.id) === funcionId);
  const necesitaSeccion = funcion?.codigo === PROMPT_GENERICO;
  const completo = funcion !== undefined && (!necesitaSeccion || seccion !== "") && elegidos.length > 0;

  function alternar(id: number) {
    setElegidos((actuales) =>
      actuales.includes(id) ? actuales.filter((x) => x !== id) : actuales.length >= TOPE_DE_CASOS ? actuales : [...actuales, id],
    );
  }

  function enviar() {
    if (!funcion) return;

    crear.mutate(
      { funcion: funcion.id, ...(necesitaSeccion ? { item_codigo: seccion } : {}), casos: elegidos },
      {
        onSuccess: (experimento) => {
          onLanzado(experimento.id);
          onCerrar();
        },
        onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos lanzar el experimento.")),
      },
    );
  }

  return (
    <form
      noValidate
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (completo && !crear.isPending) enviar();
      }}
    >
      <DialogHeader>
        <DialogTitle>Nuevo experimento</DialogTitle>
        <DialogDescription>
          Se redacta la misma sección con el borrador y con la versión publicada, en cada caso elegido, para compararlas.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-1.5">
        <Label htmlFor="exp-prompt">Prompt</Label>
        <NativeSelect id="exp-prompt" value={funcionId} onChange={(e) => setFuncionId(e.target.value)}>
          <option value="">Elige un prompt con borrador</option>
          {probables.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </NativeSelect>
        {prompts.data && probables.length === 0 ? (
          <p className="text-meta text-ink-medio">Ningún prompt tiene un borrador por probar. Edita uno en la pestaña Prompts.</p>
        ) : null}
      </div>

      {necesitaSeccion ? (
        <div className="grid gap-1.5">
          <Label htmlFor="exp-seccion">Sección</Label>
          <NativeSelect id="exp-seccion" value={seccion} onChange={(e) => setSeccion(e.target.value)}>
            <option value="">Elige la sección con que probarlo</option>
            {(secciones.data ?? []).map((s) => (
              <option key={s.codigo} value={s.codigo}>
                {s.nombre}
              </option>
            ))}
          </NativeSelect>
        </div>
      ) : null}

      <fieldset className="grid gap-2">
        <legend className="text-xs font-bold">
          Casos <span className="text-ink-medio font-mono">{elegidos.length}/{TOPE_DE_CASOS}</span>
        </legend>
        {casos.data?.length === 0 ? (
          <p className="text-meta text-ink-medio">Todavía no hay casos. Congela un informe en la pestaña Casos de prueba.</p>
        ) : (
          <ul className="grid max-h-56 gap-1.5 overflow-y-auto">
            {(casos.data ?? []).map((c) => (
              <li key={c.id}>
                <label className="flex items-center gap-2.5 text-[0.8125rem] font-semibold">
                  <Checkbox checked={elegidos.includes(c.id)} onCheckedChange={() => alternar(c.id)} aria-label={c.nombre} />
                  {c.nombre}
                  {c.etiqueta ? <span className="text-ink-medio font-mono text-[0.6875rem]">{c.etiqueta}</span> : null}
                </label>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!completo || crear.isPending}>
          Correr experimento
        </Button>
      </DialogFooter>
    </form>
  );
}
